import React, { useEffect, useState } from 'react';
import { apiRequest } from '../config/api';
import LoadingSpinner from './LoadingSpinner';
import { getChannelConfig, formatChannelValue } from '../utils/sensorUtils';
import { buildFlightMeasurementRows } from '../utils/flightMeasurements';

interface MeasurementResult {
  timestamp: string;
  channel: string;
  value: number;
  aircraftId: string;
  flightTrackId: string;
}

interface Coordinate {
  latitude: number;
  longitude: number;
  altitude: number;
  timestamp: string;
}

interface Props {
  flightTrackId: string;
  dateStart: string;
  dateEnd: string | undefined;
  coordinates?: Coordinate[];
  scrollToTimestamp?: { time: number; trigger: number } | number | null;
  maxHeight?: string;
}

const SensorMeasurements: React.FC<Props> = ({
  flightTrackId,
  dateStart,
  dateEnd,
  coordinates,
  scrollToTimestamp,
  maxHeight = '480px',
}) => {
  const [measurements, setMeasurements] = useState<MeasurementResult[]>([]);
  const [loading, setLoading] = useState(true);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fetchMeasurements = async () => {
      try {
        const res = await apiRequest(`/sensor-data/${flightTrackId}`);
        if (!res.ok) return;
        const data = await res.json();
        setMeasurements(data.measurements || []);
      } catch {
        // silently fail — sensor data is optional
      } finally {
        setLoading(false);
      }
    };
    fetchMeasurements();
  }, [flightTrackId]);

  const { rows, channels } = React.useMemo(
    () => buildFlightMeasurementRows(dateStart, dateEnd, coordinates, measurements),
    [dateStart, dateEnd, coordinates, measurements]
  );

  useEffect(() => {
    if (scrollToTimestamp === null || scrollToTimestamp === undefined) return;
    const targetTime = typeof scrollToTimestamp === 'number' ? scrollToTimestamp : scrollToTimestamp.time;
    if (!rows || rows.length === 0) return;

    // Floor targetTime to the exact second matching what the diagram tooltip displays (hh:mm:ss)
    const targetSec = Math.floor(targetTime / 1000) * 1000;

    // Find the row matching the exact second
    let low = 0;
    let high = rows.length - 1;
    let bestIdx = 0;
    let bestDist = Math.abs(rows[0].tsMs - targetSec);

    while (low <= high) {
      const mid = (low + high) >> 1;
      const rowSec = rows[mid].tsMs;
      if (rowSec === targetSec) {
        bestIdx = mid;
        break;
      }
      const dist = Math.abs(rowSec - targetSec);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = mid;
      }
      if (rowSec < targetSec) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    const rowEl = document.getElementById(`meas-row-${bestIdx}`);
    const container = tableContainerRef.current;
    if (container && rowEl) {
      const th = container.querySelector('thead th');
      const containerRect = container.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();

      // The visible rows area extends from the bottom of the sticky header to the container bottom
      const headerBottomY = th ? th.getBoundingClientRect().bottom : containerRect.top;
      const scrollbarHeight = Math.max(0, container.offsetHeight - container.clientHeight);
      const visibleBottomY = containerRect.bottom - scrollbarHeight;

      // Exact vertical midpoint of the visible rows area
      const targetCenterY = headerBottomY + (visibleBottomY - headerBottomY) / 2;
      // Exact vertical midpoint of the target row
      const currentCenterY = rowRect.top + rowRect.height / 2;

      // Shift container scroll by exact delta so row centerline lands dead center
      const delta = currentCenterY - targetCenterY;
      const targetScrollTop = Math.max(0, container.scrollTop + delta);

      const triggerBlink = () => {
        container.querySelectorAll('.row-blink-highlight').forEach(el => el.classList.remove('row-blink-highlight'));
        void rowEl.offsetWidth; // Force reflow to cleanly restart the CSS animation
        rowEl.classList.add('row-blink-highlight');
        rowEl.addEventListener(
          'animationend',
          () => {
            rowEl.classList.remove('row-blink-highlight');
          },
          { once: true }
        );
      };

      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
      container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      // Start blinking only after smooth scrolling has finished
      if (Math.abs(container.scrollTop - targetScrollTop) < 3) {
        triggerBlink();
      } else {
        let finished = false;
        let fallbackTimer: any;
        const onDone = () => {
          if (finished) return;
          finished = true;
          container.removeEventListener('scrollend', onDone);
          clearTimeout(fallbackTimer);
          triggerBlink();
        };

        container.addEventListener('scrollend', onDone, { once: true });
        fallbackTimer = setTimeout(onDone, 500);
      }
    }
  }, [scrollToTimestamp, rows]);

  if (loading) return <LoadingSpinner message="Lade Messungen..." />;

  if (measurements.length === 0) {
    return <p style={{ color: '#a0afc0', fontSize: '14px', margin: 0 }}>Keine Messungen vorhanden.</p>;
  }

  const formatTimestamp = (tsMs: number) => {
    const d = new Date(tsMs);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const hasCoords = coordinates && coordinates.length > 0;

  return (
    <div ref={tableContainerRef} style={{ position: 'relative', maxHeight, overflowY: 'auto' }}>
      <style>{`
        @keyframes rowBlinkTwice {
          0% { background-color: transparent; }
          22% { background-color: #b9d7fc; }
          45% { background-color: transparent; }
          70% { background-color: #b9d7fc; }
          100% { background-color: transparent; }
        }
        .row-blink-highlight td {
          animation: rowBlinkTwice 1.6s ease-in-out !important;
        }
      `}</style>
      <table className="table table-sm" style={{ fontSize: '13px', marginBottom: 0 }}>
        <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
          <tr>
            <th style={{ color: '#687088', fontWeight: 600, border: 'none' }}>Zeitstempel</th>
            {hasCoords && <th style={{ color: '#687088', fontWeight: 600, border: 'none' }}>Breitengrad</th>}
            {hasCoords && <th style={{ color: '#687088', fontWeight: 600, border: 'none' }}>Längengrad</th>}
            {hasCoords && <th style={{ color: '#687088', fontWeight: 600, border: 'none' }}>Höhe (m)</th>}
            {channels.map(ch => {
              const cfg = getChannelConfig(ch);
              return (
                <th key={ch} style={{ color: '#687088', fontWeight: 600, border: 'none' }}>
                  {cfg.label}{cfg.unit ? ` (${cfg.unit})` : ''}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            return (
              <tr key={i} id={`meas-row-${i}`}>
                <td style={{ color: '#687088', whiteSpace: 'nowrap' }}>{formatTimestamp(row.tsMs)}</td>
                {hasCoords && <td style={{ color: '#687088' }}>{row.coord ? row.coord.latitude.toFixed(5) : '–'}</td>}
                {hasCoords && <td style={{ color: '#687088' }}>{row.coord ? row.coord.longitude.toFixed(5) : '–'}</td>}
                {hasCoords && <td style={{ color: '#687088' }}>{row.coord ? row.coord.altitude.toFixed(0) : '–'}</td>}
                {channels.map(ch => (
                  <td key={ch} style={{ color: '#687088' }}>
                    {formatChannelValue(ch, row.meas[ch])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(SensorMeasurements);
